import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import './Catalogue.css';
import foxShadowImg from '../../../assets/images/home/fox_shadow_cover.jpg';
import parselingsImg from '../../../assets/images/home/parselings_cover.jpg';

const COLORS = ['#d1db4a', '#003d2d', '#00b3b9', '#517013', '#56c648', '#f9efd9'];

const HexagonExplosion = ({ x, y }) => {
  const [hexagons] = useState(() => 
    Array.from({ length: Math.floor(Math.random() * 8) + 7 }, () => ({
      id: Math.random(),
      size: Math.random() * 40 + 20, // Increased size
      angle: Math.random() * Math.PI * 2,
      speed: Math.random() * 200 + 150, // Increased speed
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      rotation: Math.random() * 360
    }))
  );

  return createPortal(
    <div className="hexagon-explosion" style={{ left: x, top: y }}>
      {hexagons.map(hex => (
        <div
          key={hex.id}
          className="explosion-hexagon"
          style={{
            '--size': `${hex.size}px`,
            '--angle': `${hex.angle}rad`,
            '--speed': `${hex.speed}px`,
            '--color': hex.color,
            '--rotation': `${hex.rotation}deg`
          }}
        />
      ))}
    </div>,
    document.body
  );
};

const GameCard = ({ to, image, title, description }) => {
  const [explosion, setExplosion] = useState(null);
  const cardRef = useRef(null);
  const isAnimatingRef = useRef(false);

  const handleCardHover = () => {
    if (isAnimatingRef.current) return; // Prevent new explosion while animating
    
    isAnimatingRef.current = true;
    const rect = cardRef.current.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;
    
    setExplosion({
      x: x + window.scrollX,
      y: y + window.scrollY,
      id: Date.now()
    });
  };

  useEffect(() => {
    if (explosion) {
      const timer = setTimeout(() => {
        setExplosion(null);
        isAnimatingRef.current = false; 
      }, 1600); 
      return () => clearTimeout(timer);
    }
  }, [explosion]);

  return (
    <>
      <Link to={to} className="game-card" onMouseEnter={handleCardHover} ref={cardRef}>
        <img src={image} alt={title} />
        <div className="game-overlay">
          <div className="game-info">
            <h3>{title}</h3>
            <p>{description}</p>
          </div>
        </div>
      </Link>
      {explosion && <HexagonExplosion key={explosion.id} x={explosion.x} y={explosion.y} />}
    </>
  );
};

const Catalogue = () => {
  const titleRef = useRef(null);
  const containerRef = useRef(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    // Main observer for triggering animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Add animation class to title
            if (titleRef.current) {
              titleRef.current.classList.add('animate');
              titleRef.current.classList.add('visible');
            }
            // Add visible class to container after a delay
            if (containerRef.current) {
              setTimeout(() => {
                containerRef.current.classList.add('visible');
              }, 500); // Delay matches the title animation
            }
            observer.unobserve(entry.target);
          }
        });
      },
      { 
        threshold: 0.15,
        rootMargin: '-100px 0px'
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <div className="catalogue-section" ref={sectionRef}>
      <h2 className="catalogue-title" ref={titleRef}>Our Games</h2>
      <div className="catalogue-container" ref={containerRef}>
        <GameCard 
          to="/foxandshadow"
          image={foxShadowImg}
          title="Fox and Shadow"
          description="Rogue-like Deckbuilder"
        />
        <GameCard 
          to="/parselings"
          image={parselingsImg}
          title="Parselings"
          description="Deck Building Tabletop RPG"
        />
      </div>
    </div>
  );
};

export default Catalogue;
